import { useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { useNativeSessionRefresh } from "@/hooks/useNativeSessionRefresh";
import { useNativeForceUpdate } from "@/hooks/useNativeForceUpdate";
import { useNativeDeepLinks } from "@/hooks/useNativeDeepLinks";
import { useNativeEdgeSwipeBack } from "@/hooks/useNativeEdgeSwipeBack";
import { NATIVE_PULL_REFRESH_PATHS, normalizePath } from "@/lib/native/nativeRoutes";
import NativeOfflineBanner from "./NativeOfflineBanner";
import NativePullToRefreshIndicator from "./NativePullToRefreshIndicator";
import { NativeForceUpdateModal } from "./NativeForceUpdateModal";
import { NativePushOptInDialog } from "./NativePushOptInDialog";
import { useNativeKeyboardScroll } from "@/hooks/useNativeKeyboardScroll";
import { useNativePushNotifications } from "@/hooks/useNativePushNotifications";

/**
 * Native-only overlays: offline banner, pull-to-refresh, status bar / keyboard.
 */
const NativeAppShell = ({ children }: { children: React.ReactNode }) => {
  const { isNative } = useNativePlatform();
  const location = useLocation();
  const queryClient = useQueryClient();
  const path = normalizePath(location.pathname);

  useNativeSessionRefresh();
  useNativeDeepLinks();
  useNativeKeyboardScroll();
  useNativeEdgeSwipeBack();
  useNativePushNotifications();
  const forceUpdate = useNativeForceUpdate();

  const ptrEnabled = isNative && NATIVE_PULL_REFRESH_PATHS.has(path);

  const onRefresh = useCallback(async () => {
    await queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey[0];
        return (
          key === "news" ||
          key === "notifications" ||
          key === "hasUnreadNotifications" ||
          key === "userDetails" ||
          key === "kyc" ||
          key === "myListings" ||
          key === "deliveryAddresses"
        );
      },
    });
  }, [queryClient]);

  const { pullDistance, refreshing } = usePullToRefresh({
    enabled: ptrEnabled,
    onRefresh,
  });

  if (!isNative) {
    return <>{children}</>;
  }

  return (
    <>
      <NativeForceUpdateModal {...forceUpdate} />
      <NativePushOptInDialog />
      <NativeOfflineBanner />
      <NativePullToRefreshIndicator
        pullDistance={pullDistance}
        refreshing={refreshing}
      />
      {children}
    </>
  );
};

export default NativeAppShell;
